import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import Header from "./Header";
import ContactForm from "./ContactForm";
import StructuredData from "./StructuredData";
import { Link, useNavigate } from 'react-router-dom';

export default function FullscreenSlider() {
  const [showContact] = useState(false);
  const navigate = useNavigate();
  const slide = {
    image: "/images/slide7.png",
    title:
      "Notre savoir-faire au profit de votre réussite",
    subtitle: "Banque. Finance. Opérations internationales.",
    description:
      "Nous accompagnons nos clients avec rigueur et vision, pour transformer chaque défi en opportunité.",
  };

  const organizationData = {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    "name": "Bankelys",
    "description": "Cabinet de conseil en finance bancaire, finance islamique, commerce international et arbitrage médiation basé à Tunis.",
    "url": "https://bankelys.com",
    "logo": "https://bankelys.com/images/logo.png",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "93 Rue de La Palestine, Beb El Bhar",
      "addressLocality": "Tunis",
      "postalCode": "1002",
      "addressCountry": "TN"
    },
    "telephone": "+216 98 222 635",
    "email": "contact@bankelys.com",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "13:00"
      }
    ],
    "sameAs": [
      "https://linkedin.com/company/bankelys",
      "https://twitter.com/bankelys",
      "https://facebook.com/bankelys"
    ],
    "areaServed": {
      "@type": "Country",
      "name": "Tunisia"
    },
    "knowsAbout": [
      "Finance bancaire",
      "Entrée en relation avec la banque",
      "Négociation des tarifs bancaires",
      "Crédit",
      "Financement",
      "Gestion de la trésorerie",
      "Finance islamique",
      "Mourabaha",
      "Moucharaka",
      "Ijara",
      "Crédit documentaire",
      "Lettre de crédit",
      "Encaissement documentaire",
      "Remise documentaire",
      "Virement",
      "Réglementation des changes",
      "Transferts internationaux",
      "Fiche d'investissement",
      "Conformité réglementaire",
      "Incoterms",
      "Arbitrage",
      "Médiation"
    ]
  };

  return (
    <div className="relative w-full h-screen font-sans overflow-hidden">
      <Helmet>
        <title>Bankelys — Finance, Commerce International & Arbitrage</title>
        <meta name="description" content="Bankelys : conseil en finance bancaire, islamique, commerce international et arbitrage médiation à Tunis. 35 ans d'expertise à votre service." />
        <link rel="canonical" href="https://bankelys.com/" />
        <meta property="og:title" content="Bankelys — Finance, Commerce International & Arbitrage" />
        <meta property="og:description" content="Experts en finance bancaire, islamique, commerce international et arbitrage médiation. Nous transformons chaque défi financier en opportunité." />
        <meta property="og:url" content="https://bankelys.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Bankelys — Finance, Commerce International & Arbitrage" />
        <meta name="twitter:description" content="Experts en finance bancaire, islamique, commerce international et arbitrage médiation. 35 ans d'expertise à Tunis." />
      </Helmet>
      <StructuredData data={organizationData} />

      {/* Background Image with smooth zoom effect */}
      <motion.img
        src={slide.image}
        alt="Bankelys — conseil en finance bancaire et commerce international à Tunis"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "brightness(1.05) contrast(1.1) saturate(1.2)" }}
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 4, ease: "easeOut" }}
      />

      {/* Gradient Overlay for better contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/70 z-10"></div>

      {/* Header */}
      <Header />

      {/* Hero Content */}
      <div className="absolute z-20 inset-0 flex flex-col justify-center items-center md:items-start px-6 md:px-20 text-white text-center md:text-left">
        {/* Subtitle */}
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="text-sm md:text-lg mb-3 uppercase tracking-widest text-white/70"
        >
          {slide.subtitle}
        </motion.span>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="text-3xl md:text-6xl font-bold max-w-4xl leading-snug drop-shadow-[0_3px_6px_rgba(0,0,0,0.5)]"
        >
          {slide.title}
        </motion.h2>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 1 }}
          className="mt-4 max-w-2xl text-base md:text-lg text-white/90 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
        >
          {slide.description}
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 1 }}
          className="mt-8 flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={() => navigate('/services')}
            className="px-8 py-3 border border-white text-white font-medium rounded-full
                       hover:bg-white hover:text-black transition-all duration-300 ease-in-out backdrop-blur-sm"
          >
            Découvrir nos services
          </button>

          <Link
  to="/contact"
  className="px-8 py-3 bg-[#B91C1C] text-white font-medium rounded-full
             hover:bg-[#a01515] border border-transparent 
             transition-all duration-300 ease-in-out backdrop-blur-sm
             inline-block text-center"
>
  Nous contacter
</Link>
        </motion.div>
      </div>

      {/* Contact Form Popup */}
      <AnimatePresence>
        {showContact && <ContactForm />}
      </AnimatePresence>
    </div>
  );
}